import { Dexie, type Table } from "dexie";
import type {
  RoundCommandEvent,
  RoundSnapshot,
} from "../../domain/round/round-reducer.js";
import type { BootstrapPayload } from "../../shared/contracts/bootstrap.js";
import { holeEntryInput } from "../lib/hole-entry-input.js";

interface LocalRoundSnapshot {
  id: string;
  accountId: string;
  roundId: string;
  snapshot: RoundSnapshot;
  updatedAt: string;
}

interface LocalRoundCommand extends RoundCommandEvent {
  accountId: string;
  status: "queued" | "sending" | "conflicted";
  enqueuedAt: string;
}

export interface LocalDeviceAccount {
  accountId: string;
  email: string;
  displayName: string;
  bootstrap: BootstrapPayload;
  verifiedAt: string;
  lastUsedAt: string;
}

export class LocalDatabase extends Dexie {
  public roundSnapshots!: Table<LocalRoundSnapshot, string>;
  public roundCommands!: Table<LocalRoundCommand, string>;
  public deviceAccounts!: Table<LocalDeviceAccount, string>;

  public constructor(name = "fairway-rivalry") {
    super(name);
    this.version(1).stores({
      roundSnapshots: "id,[accountId+roundId],accountId,roundId,updatedAt",
      roundCommands:
        "eventId,[accountId+roundId],accountId,roundId,[accountId+status],deviceSequence,enqueuedAt",
    });
    this.version(2).stores({
      roundSnapshots: "id,[accountId+roundId],accountId,roundId,updatedAt",
      roundCommands:
        "eventId,[accountId+roundId],accountId,roundId,[accountId+status],deviceSequence,enqueuedAt",
      deviceAccounts: "accountId,lastUsedAt",
    });
  }
}

export function createLocalDatabase(name?: string): LocalDatabase {
  return new LocalDatabase(name);
}

export class RoundJournal {
  public constructor(private readonly database: LocalDatabase) {}

  public async saveSnapshotAndEnqueue(
    accountId: string,
    snapshot: RoundSnapshot,
    event: RoundCommandEvent,
  ): Promise<void> {
    await this.database.transaction(
      "rw",
      this.database.roundSnapshots,
      this.database.roundCommands,
      async () => {
        await this.putSnapshot(accountId, snapshot);
        await this.enqueue(accountId, event);
      },
    );
  }

  public async enqueue(
    accountId: string,
    event: RoundCommandEvent,
  ): Promise<void> {
    const record: LocalRoundCommand = {
      ...event,
      accountId,
      status: "queued",
      enqueuedAt: new Date().toISOString(),
    };
    await this.database.roundCommands.put(record);
  }

  public async getSnapshot(
    accountId: string,
    roundId: string,
  ): Promise<RoundSnapshot | undefined> {
    const record = await this.database.roundSnapshots.get(
      snapshotKey(accountId, roundId),
    );
    return record?.snapshot;
  }

  public async saveSnapshot(
    accountId: string,
    snapshot: RoundSnapshot,
  ): Promise<void> {
    await this.putSnapshot(accountId, snapshot);
  }

  public async getLatestActiveSnapshot(
    accountId: string,
  ): Promise<RoundSnapshot | undefined> {
    const records = await this.database.roundSnapshots
      .where("accountId")
      .equals(accountId)
      .toArray();
    return records
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
      .find(
        (record) =>
          record.snapshot.state === "draft" ||
          record.snapshot.state === "active",
      )?.snapshot;
  }

  public async getLatestRecoverableSnapshot(
    accountId: string,
  ): Promise<RoundSnapshot | undefined> {
    const [records, commands] = await Promise.all([
      this.database.roundSnapshots
        .where("accountId")
        .equals(accountId)
        .toArray(),
      this.database.roundCommands
        .where("accountId")
        .equals(accountId)
        .toArray(),
    ]);
    const queuedRoundIds = new Set(commands.map((item) => item.roundId));
    return records
      .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
      .find(
        (record) =>
          queuedRoundIds.has(record.roundId) ||
          record.snapshot.state === "draft" ||
          record.snapshot.state === "active",
      )?.snapshot;
  }

  public async listQueued(
    accountId: string,
    roundId: string,
  ): Promise<RoundCommandEvent[]> {
    const records = await this.database.roundCommands
      .where("[accountId+roundId]")
      .equals([accountId, roundId])
      .toArray();
    return records
      .filter((record) => record.status === "queued")
      .sort(compareRoundCommands)
      .map(toRoundCommand);
  }

  public async acknowledge(
    accountId: string,
    eventIds: string[],
  ): Promise<void> {
    const records = await this.database.roundCommands
      .where("eventId")
      .anyOf(eventIds)
      .and((record) => record.accountId === accountId)
      .primaryKeys();
    await this.database.roundCommands.bulkDelete(records);
  }

  /**
   * Stores the server snapshot and drops acknowledged commands. Commands
   * enqueued while the sync was in flight are newer than the server
   * snapshot, so the local snapshot that includes them is kept instead;
   * the result says which snapshot is now stored.
   */
  public async acceptServerSnapshot(
    accountId: string,
    snapshot: RoundSnapshot,
    sync: { sentEventIds: string[]; acknowledgedEventIds: string[] },
  ): Promise<"server" | "local"> {
    const sent = new Set(sync.sentEventIds);
    return this.database.transaction(
      "rw",
      this.database.roundSnapshots,
      this.database.roundCommands,
      async () => {
        const queued = await this.database.roundCommands
          .where("[accountId+roundId]")
          .equals([accountId, snapshot.roundId])
          .toArray();
        const enqueuedDuringSync = queued.some(
          (record) => !sent.has(record.eventId),
        );
        if (!enqueuedDuringSync) await this.putSnapshot(accountId, snapshot);
        await this.acknowledge(accountId, sync.acknowledgedEventIds);
        return enqueuedDuringSync ? "local" : "server";
      },
    );
  }

  public async hasUnsynced(accountId: string): Promise<boolean> {
    const count = await this.database.roundCommands
      .where("accountId")
      .equals(accountId)
      .count();
    return count > 0;
  }

  /** A delayed live GET cannot replace scores being recorded on this device. */
  public async acceptLiveSnapshot(
    accountId: string,
    snapshot: RoundSnapshot,
  ): Promise<boolean> {
    return this.database.transaction(
      "rw",
      this.database.roundSnapshots,
      this.database.roundCommands,
      async () => {
        const pending = await this.database.roundCommands
          .where("[accountId+roundId]")
          .equals([accountId, snapshot.roundId])
          .count();
        const current = await this.getSnapshot(accountId, snapshot.roundId);
        if (pending || (current && current.version >= snapshot.version))
          return false;
        await this.putSnapshot(accountId, snapshot);
        return true;
      },
    );
  }

  public async hasPendingTransfer(accountId: string): Promise<boolean> {
    return (
      (await this.database.roundCommands
        .where("accountId")
        .equals(accountId)
        .filter((record) => record.type === "scorekeeper.transferred")
        .count()) > 0
    );
  }

  public async clearAccount(accountId: string): Promise<void> {
    await this.database.transaction(
      "rw",
      this.database.roundSnapshots,
      this.database.roundCommands,
      async () => {
        await this.database.roundSnapshots
          .where("accountId")
          .equals(accountId)
          .delete();
        await this.database.roundCommands
          .where("accountId")
          .equals(accountId)
          .delete();
      },
    );
  }

  private async putSnapshot(
    accountId: string,
    snapshot: RoundSnapshot,
  ): Promise<void> {
    await this.database.roundSnapshots.put({
      id: snapshotKey(accountId, snapshot.roundId),
      accountId,
      roundId: snapshot.roundId,
      snapshot,
      updatedAt: new Date().toISOString(),
    });
  }
}

function snapshotKey(accountId: string, roundId: string): string {
  return accountId + ":" + roundId;
}

function toRoundCommand(record: LocalRoundCommand): RoundCommandEvent {
  // Older local V3 clients spread the read model's revision into this input.
  // Recover those queued scores without sending a client-owned revision.
  const payload = record.payload;
  if (
    record.type === "hole.entry.set" &&
    "entry" in payload &&
    "targetId" in payload &&
    "holeNumber" in payload
  ) {
    return {
      ...commandEnvelope(record),
      payload: {
        targetId: payload.targetId,
        holeNumber: payload.holeNumber,
        entry: holeEntryInput(payload.entry),
      },
    };
  }
  return commandEnvelope(record);
}

function commandEnvelope(record: LocalRoundCommand): RoundCommandEvent {
  return {
    eventId: record.eventId,
    roundId: record.roundId,
    deviceId: record.deviceId,
    deviceSequence: record.deviceSequence,
    baseVersion: record.baseVersion,
    type: record.type,
    occurredAt: record.occurredAt,
    payload: record.payload,
    ...(record.scorekeeperGeneration === undefined
      ? {}
      : { scorekeeperGeneration: record.scorekeeperGeneration }),
  };
}

function compareRoundCommands(
  left: LocalRoundCommand,
  right: LocalRoundCommand,
): number {
  return (
    left.baseVersion - right.baseVersion ||
    commandRank(left.type) - commandRank(right.type) ||
    left.enqueuedAt.localeCompare(right.enqueuedAt) ||
    left.deviceSequence - right.deviceSequence
  );
}

function commandRank(type: RoundCommandEvent["type"]): number {
  if (type === "round.created") return 0;
  if (type === "round.started") return 1;
  if (type === "round.finished" || type === "round.abandoned") return 3;
  return 2;
}
